import React from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { Link, useLocation } from 'react-router-dom';
import { useAccounts, useLiabilities } from '../../hooks/useData';

const navLinks = [
  { name: 'Home', icon: '🏠', path: '/' },
  { name: 'Income', icon: '💵', path: '/income' },
  { name: 'Spending', icon: '💸', path: '/spending' },
  { name: 'Budget', icon: '📊', path: '/budget' },
  { name: 'Accounts', icon: '🏦', path: '/accounts' },
  { name: 'Transactions', icon: '📄', path: '/transactions' },
  { name: 'Protection', icon: '🛡️', path: '/protection' },
  { name: 'Assets', icon: '📘', path: '/assets' },
  { name: 'Liabilities', icon: '📕', path: '/liabilities' },
  { name: 'Cash Flow', icon: '💚', path: '/cashflow' },
  { name: 'Wealth Plan', icon: '📈', path: '/wealth-plan' },
  { name: 'Fact Finder', icon: '📝', path: '/fact-finder' },
  { name: 'Plaid Test', icon: '🔗', path: '/plaid-test' },
];

const groupAccountsByType = (accounts, liabilities) => {
  const groups = {
    CASH: [],
    CREDIT: [],
    LOANS: [],
    TRACKING: [],
    CLOSED: []
  };

  // Process accounts
  accounts?.forEach(account => {
    switch (account.type) {
      case 'checking':
      case 'savings':
        groups.CASH.push({
          name: account.name.includes('Checking') ? 'Checking' : 
                account.name.includes('Savings') ? 'Savings' : account.name,
          balance: account.balance
        });
        break;
      case 'brokerage':
      case 'retirement':
      case 'crypto':
        groups.TRACKING.push({
          name: account.type === 'retirement' ? 'IRA' : 
                account.type === 'crypto' ? 'Crypto' : 'Brokerage',
          balance: account.balance
        });
        break;
      default:
        break;
    }
  });

  // Process liabilities
  liabilities?.forEach(liability => {
    switch (liability.type) {
      case 'credit_card':
        groups.CREDIT.push({
          name: liability.name?.includes('Sapphire') ? 'Visa' : 'Credit Card',
          balance: -liability.balance // Negative because it's debt
        });
        break;
      case 'student_loan':
        groups.LOANS.push({
          name: 'Student Loan',
          balance: -liability.balance
        });
        break;
      case 'auto_loan':
        groups.LOANS.push({
          name: 'Auto Loan',
          balance: -liability.balance
        });
        break;
      default:
        break;
    }
  });

  return Object.entries(groups).map(([label, accounts]) => ({
    label,
    accounts
  }));
};

// Web-specific Link wrapper component for React Native Web
const NavLink = ({ to, isActive, children, onPress }) => {
  if (typeof window !== 'undefined') {
    // We're on web, use react-router Link
    return (
      <Link to={to} style={{ textDecoration: 'none' }}>
        <View style={[styles.navItem, isActive && styles.navItemActive]}>
          {children}
        </View>
      </Link>
    );
  } else {
    // We're on native, use TouchableOpacity
    return (
      <TouchableOpacity style={[styles.navItem, isActive && styles.navItemActive]} onPress={onPress}>
        {children}
      </TouchableOpacity>
    );
  }
};

const Sidebar = () => {
  const location = useLocation();
  const { data: accounts, loading: accountsLoading } = useAccounts();
  const { data: liabilities, loading: liabilitiesLoading } = useLiabilities();
  
  const loading = accountsLoading || liabilitiesLoading;
  const accountGroups = loading ? [] : groupAccountsByType(accounts, liabilities);

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.logoContainer}>
            <img 
              src="/zecher-logo.png" 
              alt="Zecher"
              style={styles.logoImage}
              onError={(e) => {
                // Fallback to text logo if image fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <View style={[styles.logoFallback, { display: 'none' }]}>
              <View style={styles.logoIcon}>
                <Text style={styles.logoText}>Z</Text>
              </View>
              <Text style={styles.logoTitle}>echer</Text>
            </View>
          </View>
          
          {/* Navigation Links */}
          <View style={styles.navigation}>
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <NavLink key={link.name} to={link.path} isActive={isActive}>
                  <Text style={styles.navIcon}>{link.icon}</Text>
                  <Text style={[
                    styles.navText, 
                    isActive ? styles.navTextActive : styles.navTextDefault
                  ]}>
                    {link.name}
                  </Text>
                </NavLink>
              );
            })}
          </View>
          
          {/* Account Groups */}
          <View style={styles.accountSection}>
            {loading ? (
              <Text style={styles.loadingText}>Loading accounts...</Text>
            ) : (
              accountGroups.map((group) => (
                <View key={group.label} style={styles.accountGroup}>
                  <Text style={styles.groupLabel}>{group.label}</Text>
                  <View style={styles.accountList}>
                    {group.accounts.length === 0 ? (
                      <Text style={styles.noAccountsText}>No accounts</Text>
                    ) : (
                      group.accounts.map((acc, index) => (
                        <View key={`${acc.name}-${index}`} style={styles.accountItem}>
                          <Text style={styles.accountName}>{acc.name}</Text>
                          <Text style={[
                            styles.accountBalance,
                            acc.balance < 0 ? styles.negativeBalance : styles.positiveBalance
                          ]}>
                            {acc.balance < 0 ? '-' : ''}${Math.abs(acc.balance).toLocaleString()}
                          </Text>
                        </View>
                      ))
                    )}
                  </View>
                </View>
              ))
            )}
          </View>
        </View>
      </ScrollView>
      
      {/* Add Account Button */}
      <TouchableOpacity style={styles.addButton}>
        <Text style={styles.addButtonText}>+ Add Account</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 288, // w-72 equivalent (18rem = 288px)
    backgroundColor: '#FFFFFF',
    height: '100vh',
    borderRightWidth: 1,
    borderRightColor: '#F3F4F6',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 8,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  logoImage: {
    height: 40,
    width: 'auto',
    maxWidth: 200,
    objectFit: 'contain',
  },
  logoFallback: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoIcon: {
    width: 40,
    height: 40,
    backgroundColor: '#2563EB',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  logoText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  logoTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  navigation: {
    marginBottom: 24,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 4,
  },
  navItemActive: {
    backgroundColor: '#EFF6FF',
  },
  navIcon: {
    fontSize: 20,
    marginRight: 12,
    width: 24,
    textAlign: 'center',
  },
  navText: {
    fontSize: 16,
    fontWeight: '500',
    flex: 1,
  },
  navTextDefault: {
    color: '#374151',
  },
  navTextActive: {
    color: '#2563EB',
  },
  accountSection: {
    flex: 1,
  },
  accountGroup: {
    marginBottom: 12,
  },
  groupLabel: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 4,
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  accountList: {
    marginBottom: 4,
  },
  accountItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
    paddingVertical: 2,
  },
  accountName: {
    fontSize: 14,
    color: '#374151',
    flex: 1,
  },
  accountBalance: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'right',
  },
  positiveBalance: {
    color: '#059669',
  },
  negativeBalance: {
    color: '#DC2626',
  },
  noAccountsText: {
    fontSize: 12,
    color: '#D1D5DB',
    fontStyle: 'italic',
  },
  loadingText: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  addButton: {
    margin: 24,
    marginTop: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#EFF6FF',
    alignItems: 'center',
  },
  addButtonText: {
    color: '#2563EB',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default Sidebar; 